import Cookies from 'js-cookie'
import { Navigate, Outlet } from 'react-router-dom'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useGetProfileQuery } from '@/redux/service/profile'
import { motion } from 'framer-motion'

const PrivatePage = () => {
  const token = Cookies.get('token')
  const { isLoading, error } = useGetProfileQuery(undefined, {
    skip: !token,
  })

  if (!token) {
    return <Navigate to='/login' replace />
  }
  if (error && 'status' in error) {
    const err = error as FetchBaseQueryError

    if (
      err.status === 403 &&
      (err.data as { message?: string })?.message === 'Inactive user'
    ) {
      Cookies.remove('token')
      Cookies.remove('refreshToken')
      return <Navigate to='/login' replace />
    }
  }
  if (isLoading) {
    return (
      <div className='h-screen w-full flex flex-col items-center justify-center bg-background relative overflow-hidden'>
        {/* Animated Background Auras */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className='absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px] pointer-events-none'
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.05, 0.1, 0.05],
            x: [0, -40, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          className='absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-[140px] pointer-events-none'
        />

        <div className='relative flex flex-col items-center gap-12'>
          {/* Precision Geometric Loader */}
          <div className='relative flex items-center justify-center'>
            {/* Main Outer Arc */}
            <svg className='w-24 h-24 transform -rotate-90'>
              <circle
                cx='48'
                cy='48'
                r='44'
                stroke='currentColor'
                strokeWidth='0.5'
                fill='transparent'
                className='text-primary/10'
              />
              <motion.circle
                cx='48'
                cy='48'
                r='44'
                stroke='currentColor'
                strokeWidth='2'
                fill='transparent'
                strokeDasharray='60 180'
                strokeLinecap='round'
                className='text-primary'
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              />
            </svg>

            {/* Middle Segmented Ring */}
            <div className='absolute inset-0 flex items-center justify-center'>
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                className='w-16 h-16 rounded-full border border-dashed border-primary/30'
              />
            </div>

            {/* Inner Morphing Core */}
            <div className='absolute inset-0 flex items-center justify-center'>
              <motion.div
                animate={{
                  scale: [0.8, 1, 0.8],
                  borderRadius: [
                    '30% 70% 70% 30% / 30% 30% 70% 70%',
                    '50% 50% 50% 50%',
                    '70% 30% 30% 70% / 70% 70% 30% 30%',
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className='w-8 h-8 bg-primary/20 backdrop-blur-md border border-primary/40 flex items-center justify-center'
              >
                <div className='w-1.5 h-1.5 rounded-full bg-primary animate-pulse' />
              </motion.div>
            </div>
          </div>

          {/* High-End Typography Section */}
          <div className='flex flex-col items-center gap-3 text-center'>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className='relative'
            >
              <h2 className='text-xs font-black uppercase tracking-[0.6em] text-foreground/80'>
                Authenticating
              </h2>
              {/* Light Sweep Effect */}
              <motion.div
                animate={{ left: ['-100%', '200%'] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  repeatDelay: 1,
                }}
                className='absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-background/60 to-transparent skew-x-[-20deg] pointer-events-none'
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className='flex flex-col items-center gap-4'
            >
              <p className='text-[10px] font-bold text-muted-foreground/60 tracking-[0.2em] uppercase bg-muted/30 px-3 py-1 rounded-full border border-border/50'>
                Secure Environment v1.0
              </p>

              {/* Minimal Status Bar */}
              <div className='w-32 h-[1px] bg-border/30 relative overflow-hidden rounded-full'>
                <motion.div
                  animate={{ left: ['-100%', '100%'] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  className='absolute inset-0 w-1/2 bg-primary/50'
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    )
  }

  return <Outlet />
}

export default PrivatePage
